import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { VocabTerm } from "@/components/lesson/VocabularySection";
import type { BookInsight } from "@/components/lesson/BookInsightCard";
import type { LessonCompanyCase } from "@/components/lesson/CompanyCaseBlock";
import type { AnyValue } from "@/lib/rowTypes";

interface MasteryContent {
  vocabulary: VocabTerm[];
  bookInsights: BookInsight[];
  companyCases: LessonCompanyCase[];
  decisionPrompt: string | null;
  decisionOptions: { text: string; reflection?: string }[] | null;
  completionTakeaway: string | null;
  masteryPrinciple: string | null;
  adaptiveData: {
    simplified_explanation: string | null;
    standard_explanation: string | null;
    advanced_explanation: string | null;
    parent_discussion_prompt: string | null;
    parent_reinforcement_note: string | null;
    current_example_title: string | null;
    current_example_body: string | null;
    current_example_why_it_matters: string | null;
    example_date: string | null;
    current_example_source_type: string | null;
    is_current_example_active: boolean;
    decision_prompt_11_13: string | null;
    decision_prompt_14_16: string | null;
    decision_prompt_17_plus: string | null;
    age_band_mode_enabled: boolean;
  } | null;
  loading: boolean;
}

export function useMasteryContent(lessonId: string | undefined): MasteryContent {
  const [vocabulary, setVocabulary] = useState<VocabTerm[]>([]);
  const [bookInsights, setBookInsights] = useState<BookInsight[]>([]);
  const [companyCases, setCompanyCases] = useState<LessonCompanyCase[]>([]);
  const [decisionPrompt, setDecisionPrompt] = useState<string | null>(null);
  const [decisionOptions, setDecisionOptions] = useState<MasteryContent["decisionOptions"]>(null);
  const [completionTakeaway, setCompletionTakeaway] = useState<string | null>(null);
  const [masteryPrinciple, setMasteryPrinciple] = useState<string | null>(null);
  const [adaptiveData, setAdaptiveData] = useState<MasteryContent["adaptiveData"]>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lessonId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);

      const [vocabRes, insightsRes, casesRes, lessonRes] = await Promise.all([
        supabase
          .from("lesson_vocabulary")
          .select("id, term, definition, example, sort_order")
          .eq("lesson_id", lessonId)
          .order("sort_order"),
        supabase
          .from("lesson_book_insights")
          .select("id, insight_type, perspective, why_it_matters, mastery_principle, is_primary, sort_order, book_id")
          .eq("lesson_id", lessonId)
          .order("sort_order"),
        supabase
          .from("lesson_company_cases")
          .select("id, what_happened, why_it_matters_here, what_this_proves, decision_prompt, sort_order, company_case_id")
          .eq("lesson_id", lessonId)
          .order("sort_order"),
        supabase
          .from("lessons")
          .select("decision_prompt_text, decision_options, completion_takeaway, mastery_principle, simplified_explanation, standard_explanation, advanced_explanation, parent_discussion_prompt, parent_reinforcement_note, current_example_title, current_example_body, current_example_why_it_matters, example_date, current_example_source_type, is_current_example_active, decision_prompt_11_13, decision_prompt_14_16, decision_prompt_17_plus, age_band_mode_enabled")
          .eq("lesson_id", lessonId)
          .maybeSingle(),
      ]);

      // Vocabulary
      setVocabulary(
        ((vocabRes.data) || []).map((v: AnyValue) => ({
          id: v.id,
          term: v.term,
          definition: v.definition,
          example: v.example,
        }))
      );

      // Book insights — need to fetch book details
      const insightsData = (insightsRes.data) || [];
      if (insightsData.length > 0) {
        const bookIds = [...new Set(insightsData.map((i: AnyValue) => i.book_id))];
        const { data: booksData } = await supabase
          .from("books")
          .select("id, title, author, core_thesis")
          .in("id", bookIds);
        
        const booksMap = new Map((booksData || []).map((b: AnyValue) => [b.id, b]));
        
        setBookInsights(
          insightsData.map((i: AnyValue) => {
            const book = booksMap.get(i.book_id) || { title: "Unknown", author: "Unknown" };
            return {
              id: i.id,
              insight_type: i.insight_type,
              perspective: i.perspective,
              why_it_matters: i.why_it_matters,
              mastery_principle: i.mastery_principle,
              is_primary: i.is_primary,
              book: {
                title: book.title,
                author: book.author,
                core_thesis: book.core_thesis,
              },
            };
          })
        );
      } else {
        setBookInsights([]);
      }

      // Company cases — need to fetch company details
      // Defensive dedup: even with the DB unique index, dedupe by company_case_id
      // so a single lesson never renders the same case study twice.
      const rawCases = (casesRes.data) || [];
      const seenCaseIds = new Set<string>();
      const casesData = rawCases.filter((c: AnyValue) => {
        const key = c.company_case_id ?? c.id;
        if (seenCaseIds.has(key)) return false;
        seenCaseIds.add(key);
        return true;
      });
      if (casesData.length > 0) {
        const companyIds = [...new Set(casesData.map((c: AnyValue) => c.company_case_id))];
        const { data: companiesData } = await supabase
          .from("company_cases")
          .select("id, company_name, industry")
          .in("id", companyIds);
        
        const companiesMap = new Map((companiesData || []).map((c: AnyValue) => [c.id, c]));
        
        setCompanyCases(
          casesData.map((c: AnyValue) => {
            const company = companiesMap.get(c.company_case_id) || { company_name: "Unknown" };
            return {
              id: c.id,
              what_happened: c.what_happened,
              why_it_matters_here: c.why_it_matters_here,
              what_this_proves: c.what_this_proves,
              decision_prompt: c.decision_prompt,
              company: {
                company_name: company.company_name,
                industry: company.industry,
              },
            };
          })
        );
      } else {
        setCompanyCases([]);
      }

      // Lesson-level fields
      if (lessonRes.data) {
        const ld = lessonRes.data;
        setDecisionPrompt(ld.decision_prompt_text || null);
        setDecisionOptions(Array.isArray(ld.decision_options) ? (ld.decision_options as AnyValue) : null);
        setCompletionTakeaway(ld.completion_takeaway || null);
        setMasteryPrinciple(ld.mastery_principle || null);
        setAdaptiveData({
          simplified_explanation: ld.simplified_explanation || null,
          standard_explanation: ld.standard_explanation || null,
          advanced_explanation: ld.advanced_explanation || null,
          parent_discussion_prompt: ld.parent_discussion_prompt || null,
          parent_reinforcement_note: ld.parent_reinforcement_note || null,
          current_example_title: ld.current_example_title || null,
          current_example_body: ld.current_example_body || null,
          current_example_why_it_matters: ld.current_example_why_it_matters || null,
          example_date: ld.example_date || null,
          current_example_source_type: ld.current_example_source_type || null,
          is_current_example_active: ld.is_current_example_active || false,
          decision_prompt_11_13: ld.decision_prompt_11_13 || null,
          decision_prompt_14_16: ld.decision_prompt_14_16 || null,
          decision_prompt_17_plus: ld.decision_prompt_17_plus || null,
          age_band_mode_enabled: ld.age_band_mode_enabled || false,
        });
      }

      setLoading(false);
    };

    void load();
  }, [lessonId]);

  return { vocabulary, bookInsights, companyCases, decisionPrompt, decisionOptions, completionTakeaway, masteryPrinciple, adaptiveData, loading };
}
